import { apiFetch } from '../../utils/apiClient';
import React, { useState, useEffect, useRef, useCallback } from 'react';

const API  = '/api';
const mono = { fontFamily: 'JetBrains Mono, monospace' };
const panel = { background: '#141720', border: '1px solid #ffffff14', borderRadius: 8, padding: '14px 16px' };
const SEV_COLOR = { CRITICAL: '#ff0040', HIGH: '#ff3b4e', MEDIUM: '#f59e0b', LOW: '#10b981' };
const POLL_MS = 500;

const DATASETS = {
  iiot: { key:'iiot', label:'CICAPT-IIoT', headerLabel:'LIVE APT STREAM — CICAPT-IIoT', totalRows:9_536_823, fileCount:1, detection:'Hybrid AE + LightGBM', startEndpoint:'/stream/cicapt', accentColor:'#3b82f6', accentBg:'#3b82f618', accentBorder:'#3b82f644', accentText:'#60a5fa', doneColor:'#10b981', files:['Phase1-Mon','Phase1-Tue','Phase1-Wed','Phase1-Thu','Phase1-Fri','Phase2-Mon','Phase2-Tue','Phase2-Wed','Phase2-Thu','Phase2-Fri','Phase2-Sat','Phase2-Sun'] },
};

function formatNum(n)  { return (n||0).toLocaleString(); }
function formatTime(s) { if(!s) return '0s'; if(s<60) return `${s}s`; return `${Math.floor(s/60)}m ${s%60}s`; }

function jobToMetrics(job, ds) {
  if (!job) return null;
  return {
    total_attacks: job.attacks||0, total_normal: job.normal||0, incidents: job.incidents||0,
    severity_distribution: job.severity||{CRITICAL:0,HIGH:0,MEDIUM:0,LOW:0},
    attack_types: buildAttackTypes(job.alerts_sample||[]),
    hourly_distribution: buildHourly(job.alerts_sample||[]),
    rows_processed: job.rows_processed||0, rows_total: job.rows_total||ds.totalRows, dataset: ds.key,
    _stream: { pct:job.pct||0, current_file:job.current_file||'', file_index:job.file_index||0, file_total:job.file_total||ds.fileCount, elapsed:job.elapsed||0, status:job.status },
  };
}
function buildAttackTypes(sample) { const c={}; (sample||[]).forEach(a=>{const t=a.attack_type||'Unknown';c[t]=(c[t]||0)+1;}); return Object.entries(c).map(([type,count])=>({type,count})).sort((a,b)=>b.count-a.count); }
function buildHourly(sample) { const h=Array(24).fill(0); (sample||[]).forEach(a=>{try{const x=new Date(a.timestamp).getHours();if(x>=0&&x<24)h[x]++;}catch{}}); return h; }

function Btn({onClick,border,bg,color,children,style={}}) { return <button onClick={onClick} style={{padding:'6px 14px',borderRadius:5,fontSize:11,cursor:'pointer',border,background:bg,color,...mono,...style}}>{children}</button>; }
function DatasetTab({ds,active,disabled,onSelect}) {
  return <button onClick={()=>!disabled&&onSelect(ds.key)} disabled={disabled} style={{padding:'5px 12px',borderRadius:5,fontSize:10,cursor:disabled?'not-allowed':'pointer',border:active?`1px solid ${ds.accentBorder}`:'1px solid #ffffff14',background:active?ds.accentBg:'transparent',color:active?ds.accentText:'#4b5563',opacity:disabled?0.5:1,...mono,transition:'all 0.2s'}}>{ds.label}</button>;
}

export default function LiveStream({ onLiveUpdate, onComplete }) {
  const [datasetKey,setDatasetKey] = useState('iiot');
  const [job,setJob]               = useState(null);
  const [running,setRunning]       = useState(false);
  const [paused,setPaused]         = useState(false);
  const [done,setDone]             = useState(false);
  const [error,setError]           = useState('');
  const pollRef = useRef(null);
  const ds = DATASETS[datasetKey];

  const startPolling = useCallback(() => {
    if(pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async()=>{
      try {
        const res=await fetch(`${API}/stream/status`); const data=await res.json(); setJob(data);
        if(onLiveUpdate) onLiveUpdate(jobToMetrics(data,ds),data.alerts_sample||[]);
        if(data.status==='complete'){clearInterval(pollRef.current);pollRef.current=null;setRunning(false);setPaused(false);setDone(true);if(onComplete)onComplete();}
        else if(data.status==='paused') {setPaused(true);setRunning(false);}
        else if(data.status==='running'){setPaused(false);setRunning(true);}
        else if(data.status==='error'||data.status==='stopped'){clearInterval(pollRef.current);pollRef.current=null;setRunning(false);setPaused(false);if(data.status==='error')setError(data.error||'Stream error');}
      } catch(err){console.warn('[LiveStream] poll error:',err.message);}
    }, POLL_MS);
  },[onLiveUpdate,onComplete,ds]);

  const stopPolling = useCallback(()=>{if(pollRef.current){clearInterval(pollRef.current);pollRef.current=null;}},[]);

  useEffect(()=>{
    fetch(`${API}/stream/status`).then(r=>r.json()).then(d=>{
      if(d.status==='running'||d.status==='paused'){
        if(d.dataset&&DATASETS[d.dataset]) setDatasetKey(d.dataset);
        setJob(d);
        if(d.status==='running'){setRunning(true);setPaused(false);}
        if(d.status==='paused'){setRunning(false);setPaused(true);}
        if(onLiveUpdate) onLiveUpdate(jobToMetrics(d,ds),d.alerts_sample||[]);
        startPolling();
      }
    }).catch(()=>{});
  },[]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>()=>stopPolling(),[stopPolling]);

  async function startStream(){setError('');setDone(false);setJob(null);setRunning(true);setPaused(false);stopPolling();if(onLiveUpdate)onLiveUpdate(null,[]);try{const res=await apiFetch(ds.startEndpoint,{method:'POST'});const data=await res.json();if(data.status==='error'){setError(data.message||'Failed to start');setRunning(false);return;}await new Promise(r=>setTimeout(r,400));startPolling();}catch(err){setError(`Failed to start stream: ${err.message}`);setRunning(false);}}
  async function pauseStream()  {await apiFetch('/stream/pause',{method:'POST'}).catch(()=>{});setPaused(true);setRunning(false);}
  async function resumeStream() {await apiFetch('/stream/resume',{method:'POST'}).catch(()=>{});setPaused(false);setRunning(true);}
  async function stopStream()   {await apiFetch('/stream/stop',{method:'POST'}).catch(()=>{});stopPolling();setRunning(false);setPaused(false);}
  function resetStream(){stopPolling();setJob(null);setDone(false);setError('');setRunning(false);setPaused(false);if(onLiveUpdate)onLiveUpdate(null,[]);}
  function handleDatasetChange(key){if(running||paused)stopStream();resetStream();setDatasetKey(key);}

  const fileIdx=job?.file_index||0, liveFiles=job?.file_total||ds.fileCount, liveTotal=job?.rows_total||ds.totalRows;
  const pct=job?.pct!==undefined?job.pct:(fileIdx/Math.max(liveFiles,1))*100;
  const isActive=running||paused;
  const barColor=done?ds.doneColor:paused?'#f59e0b':running?ds.accentColor:'#374151';
  const attackRate=job?.rows_processed?((job.attacks/job.rows_processed)*100).toFixed(4):'0.0000';

  // Live accuracy metrics
  const knownAttacks=datasetKey==='iiot'?1004:557;
  const liveTP=Math.max(0,(job?.attacks||0)-(job?.fp_estimate||0));
  const liveFP=job?.fp_estimate||0;
  const liveTN=Math.max(0,(job?.normal||0)-liveFP);
  const liveFN=Math.max(0,knownAttacks-liveTP);
  const livePrecision=liveTP+liveFP>0?((liveTP/(liveTP+liveFP))*100).toFixed(1):'—';
  const progressFrac=Math.min(1,(job?.rows_processed||0)/Math.max(liveTotal,1));
  const liveRecall=progressFrac>=0.10&&liveTP+liveFN>0?((liveTP/(liveTP+liveFN))*100).toFixed(1):'—';
  const liveFPRpct=liveTN+liveFP>0?((liveFP/(liveTN+liveFP))*100).toFixed(4):'0.0000';
  const liveF1=(livePrecision!=='—'&&liveRecall!=='—')?(()=>{const p=parseFloat(livePrecision)/100,r=parseFloat(liveRecall)/100;return p+r>0?((2*p*r/(p+r))*100).toFixed(1):'';})():'—';
  const recallNum=parseFloat(liveRecall);
  const accColor=isNaN(recallNum)?'#4b5563':recallNum>=80?'#10b981':recallNum>=60?'#f59e0b':'#ff3b4e';
  const fprDisplay=job?.fpr_live!==undefined&&job?.fpr_live!==null?Number(job.fpr_live).toFixed(4):liveFPRpct;

  function renderControls(){
    if(done)    return(<><span style={{fontSize:10,color:ds.doneColor,...mono,alignSelf:'center'}}>✓ Complete</span><Btn onClick={resetStream} border="1px solid #ffffff22" bg="transparent" color="#9ca3af">↺ Reset</Btn><Btn onClick={startStream} border={`1px solid ${ds.accentBorder}`} bg={ds.accentBg} color={ds.accentText}>▶ Re-run</Btn></>);
    if(paused)  return(<><span style={{fontSize:10,color:'#f59e0b',...mono,alignSelf:'center'}}>⏸ Paused</span><Btn onClick={resumeStream} border="1px solid #f59e0b44" bg="#f59e0b18" color="#fbbf24">▶ Resume</Btn><Btn onClick={stopStream} border="1px solid #ff3b4e44" bg="#ff3b4e18" color="#ff6b7a">■ Stop</Btn></>);
    if(running) return(<><Btn onClick={pauseStream} border="1px solid #f59e0b44" bg="#f59e0b18" color="#fbbf24"> Pause</Btn><Btn onClick={stopStream} border="1px solid #ff3b4e44" bg="#ff3b4e18" color="#ff6b7a">■ Stop</Btn></>);
    return <Btn onClick={startStream} border={`1px solid ${ds.accentBorder}`} bg={ds.accentBg} color={ds.accentText}>▶ Start Live Stream</Btn>;
  }
  function statusLabel(){
    if(done)    return ' Stream Complete';
    if(paused)  return `⏸ Paused at file ${fileIdx}/${liveFiles} — ${job?.current_file?.replace('-no-metadata.parquet','')||'...'}`;
    if(running) return `Processing file ${fileIdx}/${liveFiles}  ${job?.current_file?.replace('-no-metadata.parquet','')||'...'}`;
    return `Ready to stream ${liveTotal.toLocaleString()} rows`;
  }
  const statusColor=done?ds.doneColor:paused?'#f59e0b':running?ds.accentText:'#6b7280';

  return (
    <div style={panel}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,gap:10,flexWrap:'wrap'}}>
        <div>
          <div style={{fontSize:11,color:'#9ca3af',...mono,letterSpacing:'.1em'}}>{ds.headerLabel}</div>
          <div style={{fontSize:10,color:'#4b5563',...mono,marginTop:2}}>{liveTotal.toLocaleString()} rows · {liveFiles} files · {ds.detection}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <div style={{display:'flex',gap:4}}>{Object.values(DATASETS).map(d=><DatasetTab key={d.key} ds={d} active={d.key===datasetKey} disabled={isActive} onSelect={handleDatasetChange}/>)}</div>
          {renderControls()}
        </div>
      </div>

      {/* Progress */}
      <div style={{marginBottom:12}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
          <span style={{fontSize:11,color:statusColor,...mono}}>{statusLabel()}</span>
          <span style={{fontSize:11,color:'#9ca3af',...mono}}>{pct.toFixed(1)}%</span>
        </div>
        <div style={{height:8,background:'#ffffff0a',borderRadius:4,overflow:'hidden'}}>
          <div style={{width:`${pct}%`,height:'100%',background:barColor,borderRadius:4,transition:'width 0.4s ease',animation:paused?'pulse 1.5s ease-in-out infinite':'none'}}/>
        </div>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
        <div style={{display:'flex',justifyContent:'space-between',marginTop:4}}>
          <span style={{fontSize:10,color:'#4b5563',...mono}}>{formatNum(job?.rows_processed)} / {formatNum(liveTotal)} rows</span>
          <span style={{fontSize:10,color:'#4b5563',...mono}}>{job?.elapsed?`⏱ ${formatTime(job.elapsed)}`:''}</span>
        </div>
      </div>

      {/* File timeline */}
      <div style={{display:'flex',gap:6,marginBottom:12}}>
        {Array.from({length:liveFiles}).map((_,i)=>{
          const completed=(fileIdx>i+1)||done,current=fileIdx===i+1&&isActive;
          const col=completed?ds.doneColor:current?(paused?'#f59e0b':ds.accentColor):'#ffffff14';
          return <div key={i} style={{flex:1}}><div style={{height:4,background:col,borderRadius:2,transition:'background 0.3s'}}/></div>;
        })}
      </div>

      {/* 4 metric cards */}
      {(isActive||done)&&job&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:12}}>
          {[['ATTACKS',formatNum(job.attacks),'#ff3b4e'],['NORMAL',formatNum(job.normal),'#10b981'],['INCIDENTS',formatNum(job.incidents),'#3b82f6'],['ATTACK RATE',`${attackRate}%`,'#f59e0b']].map(([l,v,c])=>(
            <div key={l} style={{background:'#0d1018',borderRadius:5,padding:'8px 10px',textAlign:'center'}}>
              <div style={{fontSize:9,color:'#4b5563',...mono,marginBottom:6}}>{l}</div>
              <div style={{fontSize:14,fontWeight:700,color:c,...mono}}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Live Detection Accuracy Panel */}
      {(isActive||done)&&job&&(job.rows_processed||0)>0&&(
        <div style={{background:'#0d1018',border:'1px solid #ffffff0a',borderRadius:6,padding:'10px 14px',marginBottom:12}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <span style={{fontSize:9,color:'#4b5563',...mono,letterSpacing:'.08em'}}>LIVE DETECTION ACCURACY</span>
            <span style={{fontSize:9,color:'#4b5563',...mono}}>est. · updates every 500ms</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6,marginBottom:10}}>
            {[
              {label:'RECALL (TPR)',value:liveRecall!=='—'?`${liveRecall}%`:'—',color:accColor,tip:'Attacks caught / total attacks'},
              {label:'PRECISION',value:livePrecision!=='—'?`${livePrecision}%`:'—',color:'#60a5fa',tip:'True attacks / all flagged'},
              {label:'F1 SCORE',value:liveF1!==''?`${liveF1}%`:'—',color:'#c084fc',tip:'Harmonic mean of precision & recall'},
              {label:'FPR',value:`${fprDisplay}%`,color:'#f59e0b',tip:'Benign flows incorrectly flagged'},
            ].map(({label,value,color,tip})=>(
              <div key={label} title={tip} style={{background:'#141720',borderRadius:5,padding:'7px 10px',textAlign:'center',border:'1px solid #ffffff08',cursor:'help'}}>
                <div style={{fontSize:8,color:'#4b5563',...mono,marginBottom:4}}>{label}</div>
                <div style={{fontSize:15,fontWeight:700,color,...mono}}>{value}</div>
              </div>
            ))}
          </div>
          <div>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
              <span style={{fontSize:8,color:'#4b5563',...mono}}>TP: {liveTP.toLocaleString()} · FP: {liveFP.toLocaleString()} · FN~: {liveFN.toLocaleString()}</span>
              <span style={{fontSize:8,color:accColor,...mono}}>{liveRecall!=='—'?`${liveRecall}% detected`:'warming up…'}</span>
            </div>
            <div style={{height:4,background:'#ffffff08',borderRadius:2,overflow:'hidden'}}>
              <div style={{width:`${Math.min(100,parseFloat(liveRecall)||0)}%`,height:'100%',background:accColor,borderRadius:2,transition:'width 0.6s ease, background 0.4s ease'}}/>
            </div>
          </div>
        </div>
      )}

      {/* Severity */}
      {(isActive||done)&&job?.severity&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8,marginBottom:12}}>
          {['CRITICAL','HIGH','MEDIUM','LOW'].map(s=>(
            <div key={s} style={{background:'#0d1018',borderRadius:4,padding:'6px 8px'}}>
              <div style={{fontSize:9,color:SEV_COLOR[s],...mono,marginBottom:2}}>{s}</div>
              <div style={{fontSize:12,fontWeight:700,color:SEV_COLOR[s],...mono}}>{formatNum(job.severity[s])}</div>
            </div>
          ))}
        </div>
      )}

      {/* Live feed */}
      {isActive&&job?.alerts_sample?.length>0&&(
        <div style={{background:'#0d1018',borderRadius:5,padding:'8px 10px',marginBottom:10,overflow:'hidden'}}>
          <div style={{fontSize:9,color:'#4b5563',...mono,marginBottom:6}}>LIVE DETECTIONS</div>
          {job.alerts_sample.slice(0,5).map((a,i)=>{
            const col=SEV_COLOR[(a.threat_level||'LOW').toUpperCase()]||'#6b7280';
            return(
              <div key={i} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 0',borderBottom:'1px solid #ffffff08',minWidth:0}}>
                <div style={{width:5,height:5,borderRadius:'50%',background:col,flexShrink:0}}/>
                <span style={{fontSize:10,...mono,color:'#9ca3af',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>[🔴 {a.src_ip||a.source_ip||'?'}] {a.attack_type}</span>
                <span style={{fontSize:9,color:col,...mono,flexShrink:0}}>{a.threat_level}</span>
              </div>
            );
          })}
        </div>
      )}

      {error&&<div style={{padding:'6px 10px',background:'#ff3b4e0a',border:'1px solid #ff3b4e33',borderRadius:5,fontSize:11,color:'#ff6b7a',...mono,marginTop:8}}>✗ {error}</div>}

      {done&&job&&(
        <div style={{padding:'8px 10px',background:'#10b98110',border:'1px solid #10b98133',borderRadius:5,fontSize:11,color:'#34d399',...mono,marginTop:8}}>
          ✓ {formatNum(job.rows_processed)} rows · {formatNum(job.attacks)} attacks · {job.incidents} incidents · {formatTime(job.elapsed)}
        </div>
      )}
    </div>
  );
}
